import React, { useState, useEffect, useRef } from 'react';
import Plot from 'react-plotly.js';
import { toPng } from 'html-to-image';
import { useTheme } from "../../context/ThemeProvider";
import { getColorSchemePalette } from "../../context/colorSchemes";
import useCopyToClipboard from "../../hooks/useCopyToClipboard";
import VizCopyButton from "../shared/Viz/VizCopyButton";
import VizDownloadButton from "../shared/Viz/VizDownloadButton";
import VizActionBar from "../shared/Viz/VizActionBar";
import VizErrorBoundary from "../shared/Viz/VizErrorBoundary";
import InlineSpinner from "../shared/Spinner/InlineSpinner";
import { useDiagramExport } from '../../hooks/useDiagramExport';
import useNativeFullscreen from '../../hooks/useNativeFullscreen';
import logger from '../../services/Logger';
import { useOpenInNebula } from "../../hooks/useOpenInNebula";

// Defined outside the component so the object reference is stable across renders.
// A new object on every render causes react-plotly to re-initialise the modebar,
// which briefly shows Plotly's native zoom button before hiding it again.
const PLOT_CONFIG = {
  responsive: true,
  displayModeBar: false,
  displaylogo: false,
  modeBarButtons: [],
};

const PhysicsViz = ({ code, hideControls = false, toggleElement = null }) => {
  const [vizData, setVizData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { theme, colorScheme } = useTheme();
  const palette = getColorSchemePalette(colorScheme, theme);
  const { copied, copyToClipboard } = useCopyToClipboard({ caller: "PhysicsViz" });

  const containerRef = useRef(null);
  const openInNebula = useOpenInNebula();
  const { isFullscreen, enterFullscreen } = useNativeFullscreen(containerRef);
  useDiagramExport(containerRef, code, { delay: 500, trigger: vizData, enabled: !!vizData });

  const handleCopy = () => copyToClipboard(code);
  const handleDownload = () => {
    if (!containerRef.current) return;
    toPng(containerRef.current, { cacheBust: true, pixelRatio: 2, skipFonts: true })
      .then((dataUrl) => {
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `chart-${Date.now()}.png`;
        a.click();
      })
      .catch((err) => logger.warn('PhysicsViz', 'Failed to download PNG', { error: err.message }));
  };

  useEffect(() => {
    if (!code) return;

    // Sanitize literal control characters that LLMs sometimes emit inside JSON
    // string values instead of proper escape sequences (\n, \t, etc.).
    // The regex matches each JSON string token and replaces any bare control
    // characters (U+0000–U+001F) with their JSON escape equivalents.
    const sanitize = (raw) => raw.replace(
      /"((?:[^"\\]|\\.)*)"/g,
      // eslint-disable-next-line no-control-regex
      (match) => match.replace(/[\x00-\x1f]/g, (c) => {
        const escapes = { '\n': '\\n', '\r': '\\r', '\t': '\\t', '\b': '\\b', '\f': '\\f' };
        return escapes[c] ?? `\\u${c.charCodeAt(0).toString(16).padStart(4, '0')}`;
      })
    );

    try {
      const parsed = JSON.parse(sanitize(code));
      setVizData(parsed);
      setError(null);
      setIsLoading(false);
    } catch (err) {
      // If parsing fails, check if it looks like an incomplete stream
      // If it doesn't end with '}', it's likely still streaming
      const trimmed = code.trim();
      if (!trimmed.endsWith('}')) {
        setIsLoading(true);
        setError(null);
      } else {
        setError("Invalid JSON for visualization");
        setIsLoading(false);
        logger.error('PhysicsViz', 'Invalid JSON for visualization', { error: err.message });
      }
    }
  }, [code]);

  // Stability guard: if `code` hasn't changed for 1.5s, force-clear the loading
  // placeholder so it never gets stuck on "Generating visualization..." after
  // streaming has actually finished. The error/data state set above is preserved.
  useEffect(() => {
    const t = setTimeout(() => {
      setIsLoading((prev) => (prev ? false : prev));
    }, 1500);
    return () => clearTimeout(t);
  }, [code]);

  if (isLoading) {
    return (
      <div className="w-full my-6 border border-edge-gray-200 dark:border-edge-gray-700 rounded-xl overflow-hidden bg-fill-white dark:bg-fill-canvas-page-surface shadow-sm p-8 flex flex-col items-center justify-center text-ink-gray-400 animate-pulse">
        <InlineSpinner size="lg" className="text-ink-blue-500 mb-2" />
        <span className="text-sm font-medium text-ink-canvas-accent">Beaming…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-fill-red-50 dark:bg-fill-red-900/20 border border-edge-red-200 dark:border-edge-red-800 rounded-lg text-ink-red-600 dark:text-ink-red-400 text-sm font-mono">
        <p className="font-bold mb-1">Visualization Error</p>
        {error}
        <pre className="mt-2 text-xs opacity-75 overflow-x-auto">{code}</pre>
      </div>
    );
  }

  if (!vizData) return null;

  // Detect chart family for layout/size overrides
  const isGantt = (vizData.data || []).some(
    (t) => t.type === 'bar' && t.orientation === 'h' && t.base !== undefined
  );
  const isSankey = (vizData.data || []).some((t) => t.type === 'sankey');

  // Dynamic height: Gantt needs a row per task; Sankey needs extra node space
  // Sum ALL y entries across all traces (AI typically uses one trace per task group,
  // so Math.max would only see the largest group, not the full task count)
  const taskRowCount = isGantt
    ? (vizData.data || []).reduce((sum, t) => sum + (Array.isArray(t.y) ? t.y.length : 0), 0)
    : 0;
  const containerHeight = isGantt
    ? Math.min(Math.max(taskRowCount * 42 + 140, 520), 900)
    : isSankey ? 520 : 450;

  // Gantt needs wide left margin so task labels aren't clipped
  const ganttMargin = isGantt ? { t: 48, r: 20, b: 48, l: 180 } : null;

  const isDark = theme === 'dark';
  
  // App palette for trace colors — matches the brand blue-purple spectrum
  const colorway = isDark
    ? ['#60C8F0', '#4F7AFF', '#A78BFA', '#34D399', '#FB8F8F', '#FBBF24', '#F472B6']
    : ['#3B6FF5', '#44B8D8', '#7C3AED', '#059669', '#E8624A', '#D97706', '#DB2777'];

  const tickFont = { family: 'Inter, sans-serif', size: 11, color: palette.mutedForeground };

  // Default layout configuration for better theming
  const defaultLayout = {
    autosize: true,
    colorway,
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    font: {
      color: palette.foreground,
      family: 'Inter, sans-serif',
      size: 12,
    },
    margin: { t: 36, r: 16, b: 36, l: 16 },
    xaxis: {
      gridcolor: palette.border,
      zerolinecolor: palette.border,
      tickfont: tickFont,
      automargin: true,
    },
    yaxis: {
      gridcolor: palette.border,
      zerolinecolor: palette.border,
      tickfont: tickFont,
      automargin: true,
    },
    legend: {
      bgcolor: 'rgba(0,0,0,0)',
      font: { family: 'Inter, sans-serif', size: 11, color: palette.foreground }
    }
  };

  const baseFontSize = isFullscreen ? 15 : 12;
  const subFontSize = isFullscreen ? 13 : 11;

  // Merge user layout with defaults.
  // Theme-sensitive fields are placed AFTER the LLM spread so they always win
  // over hardcoded dark-mode hex values the LLM may have baked in.
  const layout = {
    ...defaultLayout,
    ...(vizData.layout || {}),
    // Always transparent so the chart blends with the message surface
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    // font.color: theme always wins over LLM-hardcoded value (e.g. "#e2e8f0")
    font: {
      ...defaultLayout.font,
      ...(vizData.layout?.font || {}),
      size: isFullscreen ? Math.max(vizData.layout?.font?.size || 0, baseFontSize) : (vizData.layout?.font?.size || baseFontSize),
      color: defaultLayout.font.color,
    },
    legend: {
      ...defaultLayout.legend,
      ...(vizData.layout?.legend || {}),
      font: {
        ...defaultLayout.legend.font,
        ...(vizData.layout?.legend?.font || {}),
        size: isFullscreen ? Math.max(vizData.layout?.legend?.font?.size || 0, subFontSize) : (vizData.layout?.legend?.font?.size || subFontSize),
        color: defaultLayout.legend.font.color,
      },
    },
    // Gantt: force wide left margin so task labels aren't clipped
    margin: ganttMargin
      ? { ...ganttMargin, ...(vizData.layout?.margin || {}) }
      : {
          ...(isFullscreen ? { t: 52, r: 24, b: 52, l: 24 } : defaultLayout.margin),
          ...(vizData.layout?.margin || {}),
        },
    xaxis: {
      ...defaultLayout.xaxis,
      ...(vizData.layout?.xaxis || {}),
      gridcolor: defaultLayout.xaxis.gridcolor,
      zerolinecolor: defaultLayout.xaxis.zerolinecolor,
      automargin: true,
      tickfont: {
        ...tickFont,
        ...(vizData.layout?.xaxis?.tickfont || {}),
        size: isFullscreen ? Math.max(vizData.layout?.xaxis?.tickfont?.size || 0, subFontSize) : (vizData.layout?.xaxis?.tickfont?.size || subFontSize),
      },
    },
    yaxis: {
      ...defaultLayout.yaxis,
      ...(vizData.layout?.yaxis || {}),
      gridcolor: defaultLayout.yaxis.gridcolor,
      zerolinecolor: defaultLayout.yaxis.zerolinecolor,
      automargin: true,
      tickfont: {
        ...tickFont,
        ...(vizData.layout?.yaxis?.tickfont || {}),
        size: isFullscreen ? Math.max(vizData.layout?.yaxis?.tickfont?.size || 0, subFontSize) : (vizData.layout?.yaxis?.tickfont?.size || subFontSize),
      },
    }
  };

  // Apply refined trace defaults — only fills in values the AI didn't specify.
  // line/scatter: thinner lines (1.5px), smooth spline curves, smaller markers (4px).
  const traces = (vizData.data || []).map((trace) => {
    const isLineLike = !trace.type || ['scatter', 'scattergl', 'scatter3d'].includes(trace.type);
    if (!isLineLike) return trace;
    return {
      ...trace,
      line: { width: 1.5, shape: 'spline', smoothing: 1.3, ...trace.line },
      marker: { size: 4, ...trace.marker },
    };
  });

  return (
    <div
      ref={containerRef}
      className={`w-full ${hideControls || isFullscreen ? 'my-0 h-full flex flex-col' : 'my-4 rounded-xl'} overflow-hidden bg-transparent relative group`}
    >
      {!isFullscreen && !hideControls && (
        <VizActionBar onFullscreen={enterFullscreen} leftContent={toggleElement} onOpenInNebula={openInNebula}>
          <VizCopyButton copied={copied} onClick={handleCopy} tooltip="Copy JSON" />
          <VizDownloadButton onClick={handleDownload} tooltip="Download PNG" />
        </VizActionBar>
      )}
      <div
        className="w-full relative"
        style={isFullscreen ? { flex: 1, minHeight: 0 } : { height: `${containerHeight}px` }}
      >
        <VizErrorBoundary title="Visualization Render Error" code={code} caller="PhysicsViz">
          <Plot
            data={traces}
            layout={{
              ...layout,
              autosize: true,
              height: undefined,
              ...(isFullscreen && isGantt ? { margin: { ...layout.margin, l: Math.max((layout.margin?.l ?? 0), 220) } } : {}),
            }}
            useResizeHandler={true}
            style={{ width: '100%', height: '100%' }}
            config={PLOT_CONFIG}
          />
        </VizErrorBoundary>
      </div>
    </div>
  );
};

export default PhysicsViz;
